import React, { useState, useContext } from "react";
import { FaUserPlus, FaTimes, FaUserFriends, FaSpinner, FaMapMarkerAlt } from "react-icons/fa";
import useEscClose from "../../hooks/useEscClose";
import SearchBar from "../UI/SearchBar";
import { API_BASE } from "../../utils/api";
import { getMyAccountId } from "../../utils/config";
import { CartContext } from "../../context/CartContext";
import { fetchSalesman } from "../../utils/fetchSalesman";

export default function CustomerSearchModal({ open, onClose, onSelect }) {
    const [keyword, setKeyword] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [minCharError, setMinCharError] = useState(false);
    const user = JSON.parse(localStorage.getItem("user"));
    const MYACCOUNT_ID = getMyAccountId();
    const { clearCart } = useContext(CartContext);
    // ESC para cerrar
    useEscClose(open, onClose);

    if (!open) return null;

    const searchCustomers = async () => {
        if (keyword.trim().length < 3) {
            setMinCharError(true);
            return;
        }
        setMinCharError(false);
        setLoading(true);
        setResults([]);
        try {
            const response = await fetch(
                `${API_BASE}/customers-ultra/api/ws/func_buscarCustomers?myaccount_id=${MYACCOUNT_ID}&company_id=${user.companyId}&branch_id=${user.bodegaId}&category=CLIENTES_EMPLEADOS&buscar=${keyword}&id_salesman=${user.idSalesman ?? ""}`
            );

            if (!response.ok) throw new Error("Error en la respuesta del servidor");

            const data = await response.json();
            setResults(data);
        } catch (error) {
            console.error("Error buscando clientes:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => e.key === "Enter" && searchCustomers();

    const handleKeywordChange = (e) => {
        setKeyword(e.target.value);
        if (minCharError) setMinCharError(false);
    };

    return (
        <div className="fixed inset-0 z-[9999] flex">

            {/* OVERLAY (A la izquierda, ocupa el espacio restante) */}
            <div
                className="flex-1 bg-black/40 cursor-pointer"
                onClick={onClose}
            ></div>

            {/* PANEL DERECHO */}
            <div
                className="
                w-full md:w-[500px]
                h-full bg-white shadow-2xl
                flex flex-col overflow-hidden
                animate-slideRight
                "
            >
                {/* HEADER */}
                <div className="flex justify-between items-center px-4 py-4 border-b bg-blue-50">
                    <h3 className="text-sm font-bold text-black tracking-wide uppercase">
                        Búsqueda de Clientes
                    </h3>

                    <button
                        className="text-gray-600 hover:text-black transition"
                        onClick={onClose}
                    >
                        <FaTimes size={22} />
                    </button>
                </div>

                {/* SEARCH BAR */}
                <SearchBar
                    value={keyword}
                    onChange={handleKeywordChange}
                    onKeyDown={handleKeyPress}
                    onSearch={searchCustomers}
                    placeholder="Buscar..."
                    autoFocus
                    variant="modal"
                />
                {minCharError && (
                    <p className="text-sx text-red-500 px-4 py-1">
                        Por favor, ingrese mínimo 3 caracteres
                    </p>
                )}

                {/* RESULTS */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">

                    {/* LOADING */}
                    {loading && (
                        <div className="flex flex-col items-center mt-10 text-gray-600">
                            <FaSpinner className="animate-spin" size={30} />
                            <p className="mt-2">Buscando clientes...</p>
                        </div>
                    )}

                    {/* SIN RESULTADOS */}
                    {!loading && results.length === 0 && (
                        <div className="text-center text-gray-500 flex flex-col items-center mt-12">
                            <FaUserFriends size={50} className="opacity-40 mb-2" />
                            <p>No se encontraron clientes</p>
                        </div>
                    )}

                    {/* LISTA DE CLIENTES */}
                    {results.map((customer) => {
                        const isShipTo = customer.codShipTo && customer.codShipTo.trim() !== "";

                        return (
                            <div
                                key={customer.idCustomer || customer.customerId + (customer.codShipTo || '')}
                                className={`
                                    flex items-center justify-between border rounded-xl p-4 transition shadow-sm bg-white group
                                    ${isShipTo
                                        ? "ml-8 border-l-2 border-l-gray-300 bg-gray-50/50 scale-[0.98]"
                                        : "border-gray-200 hover:border-brand-primary shadow-md"
                                    }
                                `}
                            >
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        {isShipTo && <FaMapMarkerAlt className="text-brand-primary text-xs flex-shrink-0" />}
                                        <h3 className={`font-bold transition ${isShipTo ? "text-gray-600 text-sm" : "text-gray-800 text-base group-hover:text-brand-primary"
                                            }`}>
                                            {isShipTo ? customer.customerId + " \u00A0\u00A0\u00A0 " : ""}      {customer.companyName}
                                        </h3>
                                    </div>

                                    <div className="text-sm text-gray-500 mt-1">
                                        <p className="flex items-center gap-1">
                                            <span className="font-medium text-gray-400">
                                                {isShipTo ? "" : "Código:"}
                                            </span>
                                            <span className="text-gray-700">
                                                {isShipTo ? "" : customer.customerId}
                                            </span>
                                        </p>

                                        <p className="italic text-xs mt-1">
                                            {customer.city} {customer.city && customer.address ? "-" : ""} {customer.address}
                                        </p>
                                    </div>
                                </div>

                                <button
                                    disabled={loading}
                                    onClick={async () => {
                                        setLoading(true);
                                        try {
                                            const existsRes = await fetch(
                                                `${API_BASE}/customers-ultra/api/ws/exists_customer_in_employee?id_customer=${customer.idCustomer}&id_ship_to=${0}`
                                            );
                                            if (!existsRes.ok) throw new Error("Error al verificar el cliente");
                                            const exists = await existsRes.json();

                                            if (!exists) {
                                                alert("No existe un usuario de e-commerce para este Cliente");
                                                return;
                                            }

                                            // Llamada a la API para obtener el CustomersDto completo
                                            const response = await fetch(
                                                `${API_BASE}/customers-ultra/api/ws/func_returnCustomer?id_customer=${customer.idCustomer}&id_ship_to=${customer.idShipTo || 0}`
                                            );

                                            if (!response.ok) throw new Error("Error al obtener detalle del cliente");

                                            const fullCustomerData = await response.json();

                                            if (user.sw1 && fullCustomerData.idSalesman) {
                                                const newSalesman = await fetchSalesman(fullCustomerData.idSalesman);
                                                if (newSalesman) {
                                                    localStorage.setItem("salesman", JSON.stringify(newSalesman));
                                                    window.dispatchEvent(new CustomEvent("salesman-updated"));
                                                }
                                            }

                                            // Enviamos el DTO completo al padre y cerramos
                                            onSelect(fullCustomerData);
                                            sessionStorage.removeItem("frequent_products_page");
                                            clearCart();
                                            localStorage.removeItem("order_express_items");
                                            localStorage.removeItem("checkout_payload");
                                            onClose();
                                        } catch (error) {
                                            console.error("Error en func_returnCustomer:", error);
                                            alert("No se pudo obtener la información detallada del cliente.");
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                    className={`
                                                    ml-4 p-2.5 transition rounded-full shadow-sm
                                                    ${loading ? "opacity-50 cursor-not-allowed" : "active:scale-95"}
                                                    ${isShipTo
                                            ? "bg-white text-gray-500 hover:bg-brand-primary hover:text-white border"
                                            : "bg-gray-100 text-gray-700 hover:bg-brand-primary hover:text-white"
                                        }
                               `}
                                    title="Seleccionar cliente"
                                >
                                    {loading ? (
                                        <FaSpinner className="animate-spin" size={isShipTo ? 18 : 22} />
                                    ) : (
                                        <FaUserPlus size={isShipTo ? 18 : 22} />
                                    )}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}